import { Button } from "@/components/ui/button";
import {
  InputOTP,
  InputOTPGroup,
  InputOTPSlot,
} from "@/components/ui/input-otp"; // Shadcn OTP input
import { Label } from "@/components/ui/label";
import { useAuth } from "../context/AuthContext"; // Relative path
import { useForm, Controller } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import * as z from "zod";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Building2, Loader2, Mail } from "lucide-react"; // Import Mail icon
const formSchema = z.object({
  code: z.string().length(6, "Verification code must be 6 digits."),
});

type VerifyEmailFormValues = z.infer<typeof formSchema>;

const VerifyEmailPage = () => {
  const { verifyEmail, resendVerificationCode, isLoading } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [email, setEmail] = useState<string>("");

  useEffect(() => {
    const searchParams = new URLSearchParams(location.search);
    const emailFromQuery = searchParams.get("email");
    if (emailFromQuery) {
      setEmail(emailFromQuery);
    } else {
      // Optional: Redirect if email is not in query, or show an error
      toast.error("Email not provided for verification.");
      navigate("/login");
    }
  }, [location.search, navigate]);

  const form = useForm<VerifyEmailFormValues>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      code: "",
    },
  });

  const onSubmit = async (values: VerifyEmailFormValues) => {
    if (!email) {
      toast.error("Email address is missing.");
      return;
    }
    try {
      await verifyEmail(email, values.code);
      // AuthContext's verifyEmail navigates to /login on success
    } catch (error) {
      // Error toast is handled by AuthContext
      console.error("Verify email page error:", error);
    }
  };

  const handleResendCode = async () => {
    if (!email) {
      toast.error("Email address is missing.");
      return;
    }
    try {
      await resendVerificationCode(email);
      // Success toast is handled by AuthContext
    } catch (error) {
      // Error toast is handled by AuthContext
      console.error("Resend code error:", error);
    }
  };

  if (!email) {
    return (
      <div className="flex flex-col items-center justify-center min-h-screen bg-gray-50 p-4">
        <p className="text-red-600">
          Email address not found for verification.
        </p>
        <Link
          to="/register"
          className="mt-4 text-propertylk-blue hover:text-propertylk-darkblue"
        >
          Go back to Register
        </Link>
      </div>
    );
  }

  return (
    <div className="bg-gray-50 flex flex-col justify-center py-12 sm:px-6 lg:px-8 min-h-screen">
      <div className="sm:mx-auto sm:w-full sm:max-w-md">
        <Link to="/" className="flex items-center justify-center gap-2 mb-4">
          <Building2 className="h-10 w-10 text-propertylk-blue" />
          <span className="font-bold text-2xl text-propertylk-darkblue">
            PropertyLK
          </span>
        </Link>
        <div className="flex flex-col items-center">
          <Mail className="h-12 w-12 text-propertylk-blue mb-3" />
        </div>
        <h2 className="text-center text-2xl font-bold text-gray-900">
          Verify Your Email Address
        </h2>
        <p className="mt-2 text-center text-sm text-gray-600">
          We've sent a 6-digit verification code to
          <br />
          <span className="font-medium">{email}</span>.
          <br />
          Please enter it below to continue.
        </p>
      </div>

      <div className="mt-8 sm:mx-auto sm:w-full sm:max-w-md">
        <div className="bg-white py-8 px-4 shadow sm:rounded-lg sm:px-10">
          <form className="space-y-8" onSubmit={form.handleSubmit(onSubmit)}>
            <div className="flex flex-col items-center">
              <Label
                htmlFor="code"
                className="text-sm font-medium text-gray-700 mb-3"
              >
                Verification Code
              </Label>
              <Controller
                control={form.control}
                name="code"
                render={({ field }) => (
                  <InputOTP
                    maxLength={6}
                    {...field}
                    containerClassName="justify-center"
                  >
                    <InputOTPGroup>
                      <InputOTPSlot index={0} />
                      <InputOTPSlot index={1} />
                      <InputOTPSlot index={2} />
                      <InputOTPSlot index={3} />
                      <InputOTPSlot index={4} />
                      <InputOTPSlot index={5} />
                    </InputOTPGroup>
                  </InputOTP>
                )}
              />
              {form.formState.errors.code && (
                <p className="mt-2 text-xs text-red-600 text-center">
                  {form.formState.errors.code.message}
                </p>
              )}
            </div>

            <div>
              <Button
                type="submit"
                className="w-full bg-propertylk-blue hover:bg-propertylk-darkblue flex items-center justify-center"
                disabled={isLoading}
              >
                {isLoading ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Verifying...
                  </>
                ) : (
                  "Verify Email"
                )}
              </Button>
            </div>
          </form>

          <div className="mt-6 text-center">
            <p className="text-sm text-gray-600">
              Didn't receive the code?{" "}
              <Button
                variant="link"
                onClick={handleResendCode}
                disabled={isLoading}
                className="font-medium text-propertylk-blue hover:text-propertylk-darkblue p-0 h-auto flex items-center"
              >
                {isLoading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                Resend code
              </Button>
            </p>
          </div>
          <div className="mt-4 text-center text-sm">
            <Link
              to="/login"
              className="text-propertylk-blue hover:text-propertylk-darkblue"
            >
              Back to Login
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
};

export default VerifyEmailPage;
